import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepairCustomerIds = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [mismatches, setMismatches] = useState([]);
  // Map of customer_id -> { selected: bool, new_id: string }
  const [decisions, setDecisions] = useState({});

  const runScan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/repair-customer-ids/scan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || 'Scan failed');
      setMismatches(data.mismatches || []);
      // Pre-fill decisions: select all non-conflicting rows, use the
      // suggested id as the editable new_id.
      const next = {};
      (data.mismatches || []).forEach((m) => {
        next[m.customer_id] = { selected: !m.conflict, new_id: m.suggested_new_id };
      });
      setDecisions(next);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); }, []);

  const handleToggle = (cid) => {
    setDecisions((prev) => ({ ...prev, [cid]: { ...prev[cid], selected: !prev[cid]?.selected } }));
  };

  const handleEditNewId = (cid, value) => {
    setDecisions((prev) => ({ ...prev, [cid]: { ...prev[cid], new_id: value } }));
  };

  const selectedCount = Object.entries(decisions).filter(([, v]) => v.selected).length;

  const apply = async () => {
    const overrides = Object.entries(decisions)
      .filter(([, v]) => v.selected && v.new_id.trim())
      .map(([cid, v]) => ({ customer_id: cid, new_id: v.new_id.trim() }));
    if (overrides.length === 0) {
      toast.error('No rows selected.');
      return;
    }
    if (!window.confirm(`Apply ${overrides.length} customer ID rename${overrides.length === 1 ? '' : 's'}?\n\nEach customer's existing project IDs will be renumbered to match. This cannot be undone.`)) {
      return;
    }
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/repair-customer-ids/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ overrides }),
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || 'Apply failed');
      const ok = data.applied || 0;
      const failed = (data.results || []).filter((r) => !r.ok);
      if (failed.length === 0) {
        toast.success(`Repaired ${ok} customer${ok === 1 ? '' : 's'}.`);
      } else {
        toast.warning(`Repaired ${ok} of ${overrides.length}. ${failed.length} failed — see results.`);
        // Show the first few failure reasons.
        failed.slice(0, 3).forEach((r) => toast.error(`${r.customer_id}: ${r.error}`, { duration: 6000 }));
      }
      await runScan();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8" data-testid="repair-customer-ids-page">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
            <h1 className="text-3xl text-gray-700 font-light italic">Repair Customer IDs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Customers imported from the old system whose <code className="bg-gray-100 px-1 rounded">customer_id</code> doesn't
              match the prefix used by their existing project IDs. Apply the suggested rename to align them — every linked
              project ID is renumbered atomically, sequence preserved.
            </p>
          </div>
          <button onClick={() => navigate('/setup')} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded border border-gray-200">
            <ArrowLeft className="w-4 h-4" /> Back to Setup
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded shadow p-8 flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
          </div>
        ) : mismatches.length === 0 ? (
          <div className="bg-white rounded shadow p-8 flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-5 h-5" /> No mismatches found. Every customer's ID matches their projects.
          </div>
        ) : (
          <>
            <div className="bg-white rounded shadow p-4 mb-4 flex items-center justify-between" data-testid="repair-summary">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{mismatches.length}</span> mismatched customer{mismatches.length === 1 ? '' : 's'} found.
                <span className="text-gray-500 ml-2">{selectedCount} selected for repair.</span>
              </div>
              <button
                onClick={apply}
                disabled={applying || selectedCount === 0}
                className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-sm flex items-center gap-2"
                data-testid="repair-apply-btn"
              >
                {applying && <Loader2 className="w-4 h-4 animate-spin" />}
                Apply Selected ({selectedCount})
              </button>
            </div>

            <div className="bg-white rounded shadow overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b-2 border-gray-200 text-gray-600">
                  <tr>
                    <th className="text-left p-3 font-normal w-10"></th>
                    <th className="text-left p-3 font-normal">Customer</th>
                    <th className="text-left p-3 font-normal">Current ID</th>
                    <th className="text-left p-3 font-normal">New ID</th>
                    <th className="text-left p-3 font-normal">Projects</th>
                    <th className="text-left p-3 font-normal">Rename Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {mismatches.map((m) => {
                    const d = decisions[m.customer_id] || { selected: false, new_id: m.suggested_new_id };
                    return (
                      <tr key={m.customer_id} className={`border-b align-top ${m.conflict ? 'bg-red-50' : ''}`} data-testid={`repair-row-${m.customer_id}`}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={!!d.selected}
                            disabled={m.conflict}
                            onChange={() => handleToggle(m.customer_id)}
                            className="w-4 h-4 disabled:cursor-not-allowed"
                            data-testid={`repair-checkbox-${m.customer_id}`}
                          />
                        </td>
                        <td className="p-3 text-gray-700">{m.name || '(no name)'}</td>
                        <td className="p-3 text-gray-700 font-mono">{m.customer_id}</td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={d.new_id}
                            onChange={(e) => handleEditNewId(m.customer_id, e.target.value)}
                            disabled={m.conflict}
                            className="border border-gray-300 rounded px-2 py-1 text-sm font-mono w-40 disabled:bg-gray-100"
                            data-testid={`repair-new-id-${m.customer_id}`}
                          />
                        </td>
                        <td className="p-3 text-gray-700">{m.project_count}</td>
                        <td className="p-3 text-gray-700">
                          {m.conflict && (
                            <div className="flex items-center gap-1 text-red-600 text-xs mb-1">
                              <AlertTriangle className="w-3.5 h-3.5" /> ID collision — manual review required.
                            </div>
                          )}
                          <ul className="space-y-0.5">
                            {m.plan.slice(0, 5).map((rn, i) => (
                              <li key={i} className={`text-xs font-mono ${rn.collides ? 'text-red-600' : 'text-gray-600'}`}>
                                {rn.from} → {rn.to}{rn.collides ? ' ⚠ collides' : ''}
                              </li>
                            ))}
                            {m.plan.length > 5 && (
                              <li className="text-xs text-gray-500 italic">…and {m.plan.length - 5} more</li>
                            )}
                          </ul>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default RepairCustomerIds;
